/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
//Custom libraries
import MiniCartPopperContent from "./MiniCartPopperContent";
import { MCW_NUMBER, ZERO_NUMBER } from "../../constants/common";
//Redux
import { numItemsSelector } from "../../redux/selectors/order";
//UI
import {
  StyledButton,
  StyledTypography,
  StyledHeaderActions,
  StyledGrid,
} from "@hcl-commerce-store-sdk/react-component";
import { StyledAccountPopper } from "components/elements";
import styled from "@mui/styled-engine-sc";
import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

interface MiniCartProps {
  id: string;
  open: boolean;
  handleClick: (e?: any) => void;
  handleClose: (e?: any) => void;
}

/**
 * MiniCart component
 * displays number of cart items icon
 * @param props
 */

export const StyledHeaderBagLogo = styled("header")`
${({ theme }) => `

  .header-mini-cart-button {
    padding: 12px 18px 12px 14px;

    &.header-mini-bag-button-languageToggle .MuiTypography-body1 {
      line-height: 26px;
    }

    img {
      height: auto;
      vertical-align: middle;
    }
  }

  .cart-icon-header {
    color: #272C35 !important;
    width: 31px;
    height: 29px;
  }

  .cart-icon-header-desktop {
    color: #272C35 !important;
    width: 22px;
    height: auto;
  }

  `}
`;



const MiniCart = React.forwardRef<HTMLButtonElement | null, MiniCartProps>((props: any, ref: any) => {
  const { id, open, handleClick, handleClose } = props;
  const { t } = useTranslation();
  const numItems = useSelector(numItemsSelector);
  const [arrowRef, setArrowRef] = useState<any>(null);
  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up("sm"));

  // keep mini-cart placement always as "bottom" and just adjust the offset from the left depending on where the anchor is
  const calcOffset = () => {
    const mcw = MCW_NUMBER;
    const left = ref?.current?.offsetLeft;
    const rc = left < mcw ? mcw - left : ZERO_NUMBER;
    return [rc, ZERO_NUMBER];
  };
  const offset = calcOffset();

  return (
    <>
      <StyledHeaderBagLogo>
        <StyledButton
          testId="header-mini-cart-button"
          ref={ref}
          className="header-mini-cart-button"
          variant="text"
          color="secondary"
          onClick={handleClick}>
          <StyledHeaderActions style={{display: "flex", alignItems: "center"}}>
            <StyledGrid>
              <img 
                src="https://dpjye2wk9gi5z.cloudfront.net/wcsstore/OnboardStorefrontAssetStore/images-site/icons/shopping-bag-2-svgrepo-com.svg"
                className={isMobile ? "bag-icon-header" : "bag-icon-header-desktop"}
              />
            </StyledGrid>
            
          </StyledHeaderActions>
        </StyledButton>

        <StyledAccountPopper
          id={id}
          open={open}
          anchorEl={ref?.current}
          onClose={handleClose}
          placement="bottom"
          modifiers={[
            {
              name: "offset",
              options: {
                offset,
              },
            },
            { name: "flip", enabled: false },
            { name: "preventOverflow", enabled: true },
            { name: "hide", enabled: false },
            {
              name: "arrow",
              enabled: true,
              options: {
                element: arrowRef,
              },
            },
          ]}
          className="mini-cart-popper">
          <span className="arrow" ref={setArrowRef} />
          <MiniCartPopperContent handleClose={handleClose} />
        </StyledAccountPopper>
      </StyledHeaderBagLogo>
    </>
  );
});

export default MiniCart;
